import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import IncomingCallAccessGate from '../../modules/calls/IncomingCallAccessGate';
import ActiveCallCard from '../../modules/calls/ActiveCallCard';
import OutgoingCallDialog from '../../modules/calls/OutgoingCallDialog';
import useFonemaOnIncomingCallAlreadyAnswered from '../fonema/actions/useFonemaOnIncomingCallAlreadyAnswered';
import useFonemaOnIncomingCallsDrained from '../fonema/actions/useFonemaOnIncomingCallsDrained';
import useWhatsappOnCallEnd from './useWhatsappOnCallEnd';
import useVoiceInitiateCallCloseSync from './useVoiceInitiateCallCloseSync';
import { mapVoiceIncomingToCallInfoData } from './callInfoDataMapper';
import { useWhatsappCallsRuntime } from './WhatsappCallsRuntimeContext';
import { stopIncomingCallRingtone } from '../shared/ui/useRingtone';
import { setFonemaManagerUiBusy } from '../../../services/fonemaCallBusyService';
import { sharingInformationService } from '../../../services/sharingInformationService';
import { canUserMakeCalls } from '../../utils/callsPermissionsUtils';
import HeaderWhatsappCallsButtonMC from '../../modules/calls/HeaderWhatsappCallsButtonMC';

const INCOMING_STACK_STYLE = {
  position: 'fixed',
  right: 8,
  top: 50,
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  maxWidth: 320,
  minWidth: 220,
  margin: 0
};

const WhatsappCallManagerView = memo(({ data, audio }) => {
  const { provider } = useWhatsappCallsRuntime();
  const currentRetailerUser = useSelector((state) => state.mainReducer?.currentRetailerUser);
  const voiceReduxActiveCall = useSelector((state) => (
    provider === 'voice' ? state.voiceReducer?.activeCall : null
  ));
  const voiceActiveCallId = voiceReduxActiveCall?.callControlId ?? null;
  const canMakeCalls = canUserMakeCalls(currentRetailerUser);
  const [fonemaActiveCall, setFonemaActiveCall] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [isOutboundDialogOpen, setIsOutboundDialogOpen] = useState(false);

  const {
    sdkReady,
    incomingPhones,
    updateIncomingPhone,
    removeIncomingPhone
  } = audio;

  const activeCall = useMemo(() => {
    if (provider === 'voice') {
      return voiceReduxActiveCall
        ? mapVoiceIncomingToCallInfoData(voiceReduxActiveCall, data.from)
        : null;
    }
    return fonemaActiveCall;
  }, [provider, voiceReduxActiveCall, data.from, fonemaActiveCall]);

  const startActiveCall = useCallback((callInfoData) => {
    if (provider !== 'fonema') return;
    setFonemaActiveCall(callInfoData);
  }, [provider]);

  const endActiveCall = useCallback(() => {
    setFonemaActiveCall(null);
  }, []);

  const closeOutboundDialog = useCallback(() => {
    setIsOutboundDialogOpen(false);
    setCustomer(null);
  }, []);

  const toggleOutboundDialog = useCallback(() => {
    setIsOutboundDialogOpen((open) => !open);
    setCustomer(null);
  }, []);

  useEffect(() => {
    if (provider !== 'voice' || !voiceActiveCallId) return undefined;
    stopIncomingCallRingtone();
    return undefined;
  }, [provider, voiceActiveCallId]);

  useVoiceInitiateCallCloseSync({
    isOpen: isOutboundDialogOpen,
    onClose: closeOutboundDialog
  });

  useWhatsappOnCallEnd({ callInfoData: activeCall, onClose: endActiveCall });

  const handleCloseIncoming = useCallback(
    (callId) => removeIncomingPhone(callId),
    [removeIncomingPhone]
  );

  const isFonemaProvider = provider === 'fonema';

  useFonemaOnIncomingCallAlreadyAnswered({
    onClose: handleCloseIncoming,
    enabled: isFonemaProvider
  });

  useFonemaOnIncomingCallsDrained({
    incomingPhones,
    onClose: handleCloseIncoming,
    enabled: isFonemaProvider
  });

  useEffect(() => {
    const subscription$ = sharingInformationService.getSubject();
    const subscription = subscription$.subscribe((infoCallData) => {
      if (infoCallData.activeCall) {
        setFonemaActiveCall(infoCallData.callInfoData);
      }
      if (infoCallData.InitiateCall) {
        if (!canUserMakeCalls(currentRetailerUser)) return;
        setCustomer(infoCallData.customer);
        setIsOutboundDialogOpen(true);
      }
    });

    return () => subscription.unsubscribe();
  }, [currentRetailerUser]);

  useEffect(() => {
    setFonemaManagerUiBusy(Boolean(activeCall) || isOutboundDialogOpen);
  }, [activeCall, isOutboundDialogOpen]);

  useEffect(() => () => setFonemaManagerUiBusy(false), []);

  if (!sdkReady && (incomingPhones?.length ?? 0) === 0) {
    return null;
  }

  const hideIncomingWhileOnVoiceCall = Boolean(voiceActiveCallId);
  const visibleIncomingPhones = hideIncomingWhileOnVoiceCall ? [] : (incomingPhones ?? []);

  return (
    <>
      <HeaderWhatsappCallsButtonMC
        incomingPhones={incomingPhones}
        activeCall={activeCall}
        updateIncomingPhone={updateIncomingPhone}
        onDismiss={handleCloseIncoming}
      />
      <div id="incoming-call" />
      {visibleIncomingPhones.length > 0 && (
        <div className="tw-wrapper" style={INCOMING_STACK_STYLE} aria-live="polite">
          {visibleIncomingPhones.map((call) => (
            <IncomingCallAccessGate
              key={`incoming-${call.callId}`}
              callInfoData={call}
              startActiveCall={startActiveCall}
              updateIncomingPhone={updateIncomingPhone}
              onDismiss={handleCloseIncoming}
            />
          ))}
        </div>
      )}
      {activeCall && (
        <ActiveCallCard
          key={`active-${activeCall.callId}`}
          callInfoData={activeCall}
          onClose={endActiveCall}
        />
      )}
      {isOutboundDialogOpen && canMakeCalls && (
        <OutgoingCallDialog
          customer={customer}
          onClose={toggleOutboundDialog}
          registerNumber={data.from}
        />
      )}
    </>
  );
});

WhatsappCallManagerView.displayName = 'WhatsappCallManagerView';

export default WhatsappCallManagerView;
